import { useState, useEffect, useMemo, useCallback } from 'react';
import { SupplierThread, ThreadMessage } from '../components/forms/outreach/types';
import { outreachClient } from '../client/outreachClient';
import { bomClient } from '../client/bomClient';
import { buildThreads, formatMessageTime, isRemoteThreadId, mapAttachments, shortlistWithoutSupplier } from './outreachMappers';
import { useOutreachThreadSend } from './useOutreachThreadSend';
import { useOutreachActions } from './useOutreachActions';
import { useOutreachComposer } from './useOutreachComposer';
import { useStableThreadOrder } from './useStableThreadOrder';
import { useSupplierContactEmail } from './useSupplierContactEmail';
import { SpecDecisionChoice, useSpecDecision } from './useSpecDecision';
import { apiErrorMessage } from '../utils/apiErrorMessage';
import { useOnRobbieApplied } from '../contexts/RobbieUiContext';
import { OutreachDraftPayload } from '../types/robbieProposal';

/** The thread a Robbie draft is for: same supplier, same component (by either id). */
const threadForDraft = (threads: SupplierThread[], draft: OutreachDraftPayload) => {
  const ids = [draft.componentKey, draft.componentId].map((v) => String(v).toLowerCase());
  return threads.find((t) =>
    t.supplierName.toLowerCase() === draft.supplierName.toLowerCase() && ids.includes(String(t.componentId).toLowerCase()));
};

// Inbound emails arrive on ai-comms' ~5 minute Gmail poll, so a relaxed UI
// poll surfaces replies without a socket.
const REPLY_POLL_INTERVAL_MS = 20000;

/**
 * Outreach state. Thread selection is owned by the URL
 * (`/rfqs/:rfqId/outreach/:threadId?`). The buyer sends QUERIES: submitting one
 * is the approval, and Robbie composes and dispatches the email server-side in
 * the same call (useOutreachSend). The analyst's action cards ride the same
 * poll; on an open conversation they only ever fill the composer, so the send
 * button stays the single place an email leaves from (useOutreachActions).
 */
export const useSupplierOutreach = (rfq: any, rfqData: any, routeThreadId?: string) => {
  const rfqBoms = rfq?.boms || rfqData?.boms || [];
  const bomId = rfqBoms.length > 0 ? (typeof rfqBoms[0] === 'string' ? rfqBoms[0] : rfqBoms[0].bomId) : null;
  const rfqId = rfq?.rfqId || rfq?.id;

  const [fetchedComponents, setFetchedComponents] = useState<any[]>([]);
  const [isLoadingBOM, setIsLoadingBOM] = useState<boolean>(!!bomId);
  const [threadMessages, setThreadMessages] = useState<Record<string, ThreadMessage[]>>({});
  const [discardError, setDiscardError] = useState<string | null>(null);
  const [isDiscardingThread, setIsDiscardingThread] = useState<boolean>(false);
  const [pollNonce, setPollNonce] = useState<number>(0);
  // A first send swaps the thread's draft-… id for the real comms thread id;
  // the caller's URL canonicalizer follows this instead of bouncing to threads[0].
  const [redirectThreadId, setRedirectThreadId] = useState<string | null>(null);
  const clearRedirectThreadId = useCallback(() => setRedirectThreadId(null), []);

  const fetchLatestBOM = useCallback(async (options?: { silent?: boolean }) => {
    if (!bomId) {
      setIsLoadingBOM(false);
      return;
    }
    if (!options?.silent) setIsLoadingBOM(true);
    try {
      const res = await bomClient.getBOMStatus(bomId);
      if (res && res.components) {
        setFetchedComponents(res.components);
      }
    } catch (err) {
      console.error('Error fetching BOM status in SupplierOutreach:', err);
    } finally {
      if (!options?.silent) setIsLoadingBOM(false);
    }
  }, [bomId]);

  useEffect(() => {
    fetchLatestBOM();
  }, [fetchLatestBOM]);

  // Background poll: refresh candidate statuses and the active conversation so
  // real supplier replies show up without any manual action.
  useEffect(() => {
    const interval = setInterval(() => setPollNonce((nonce) => nonce + 1), REPLY_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (pollNonce > 0) {
      fetchLatestBOM({ silent: true });
    }
  }, [pollNonce, fetchLatestBOM]);

  const componentsToUse = useMemo(() => {
    if (fetchedComponents.length > 0) return fetchedComponents;
    const extractedData = rfqData?.analysisData || rfqData?.extractedData || rfq?.analysisData || rfq?.extractedData;
    return extractedData?.components || [];
  }, [fetchedComponents, rfqData, rfq]);

  const builtThreads = useMemo(() => buildThreads(componentsToUse, threadMessages), [componentsToUse, threadMessages]);

  const refreshThreadsSilently = useCallback(() => fetchLatestBOM({ silent: true }), [fetchLatestBOM]);
  const sendFlow = useOutreachThreadSend({
    rfqId,
    bomId,
    setThreadMessages,
    onThreadRekeyed: setRedirectThreadId,
    refreshThreads: refreshThreadsSilently,
  });
  const contactEmail = useSupplierContactEmail(bomId, refreshThreadsSilently);
  const specDecision = useSpecDecision(rfqId, refreshThreadsSilently);

  const actionFlow = useOutreachActions(rfqId, pollNonce);

  // Sorted by what the buyer can do now, not by recency — and then held still
  // while they work, so acting on a thread never moves it under their cursor.
  const threads = useStableThreadOrder(builtThreads, actionFlow.actionIndex);

  // The URL names the thread; an unknown/absent id falls back to the first
  // thread (the caller then canonicalizes the address bar).
  const activeThread = threads.find((t) => t.id === routeThreadId) || threads[0];
  const activeThreadId = activeThread?.id;
  const activeComponentId = activeThread?.componentId;
  const activeSupplierName = activeThread?.supplierName;
  const activeThreadIsRemote = Boolean(activeThreadId && isRemoteThreadId(activeThreadId));

  const fetchActiveThreadMessages = useCallback(async () => {
    if (!rfqId || !activeThreadId || !activeThreadIsRemote) return;
    try {
      const fetched = await outreachClient.getThreadMessages(rfqId, activeComponentId || 'comp-1', activeThreadId);
      if (fetched && fetched.length > 0) {
        setThreadMessages((prev) => ({
          ...prev,
          // A pending or not-sent bubble exists only here; the poll must not erase it.
          [activeThreadId]: [...fetched.map((m: any) => ({
            id: m.id,
            sender: m.sender,
            senderName: m.sender === 'buyer' ? 'You' : (activeSupplierName || 'Supplier'),
            timestamp: formatMessageTime(m.timestamp),
            text: m.text,
            // Present on queries sent since the buyer stopped writing emails;
            // without it the bubble is the email, as it was when it was sent.
            sourceQuery: typeof m.sourceQuery === 'string' && m.sourceQuery.trim() ? m.sourceQuery : undefined,
            // Files the supplier attached, as buyer-service reported them:
            // filename, type, and whether Robbie could read the text. Never
            // the parsed text itself.
            attachments: mapAttachments(m.attachments),
            status: m.status,
            errorDetails: m.errorDetails,
          })), ...(prev[activeThreadId] || []).filter((m) => m.sendState)],
        }));
      }
    } catch (err) {
      console.error('Error fetching full thread messages:', err);
    }
  }, [rfqId, activeThreadId, activeThreadIsRemote, activeComponentId, activeSupplierName]);

  useEffect(() => {
    fetchActiveThreadMessages();
  }, [fetchActiveThreadMessages, pollNonce]);

  const composer = useOutreachComposer({
    activeThread,
    bomId,
    actions: actionFlow,
    sendAsk: sendFlow.sendAskToThread,
    isSending: sendFlow.isSendingThread(activeThread),
  });

  // A Robbie draft the buyer put in the composer. It can arrive before the
  // threads have loaded (applied on another step), so it waits for its thread,
  // then lands in that composer and the conversation opens on it.
  const [robbieDraft, setRobbieDraft] = useState<OutreachDraftPayload | null>(null);
  useOnRobbieApplied('outreach_draft', (event) => {
    const { componentId, componentKey, supplierName, threadId, text, mode } = event;
    setRobbieDraft({ componentId, componentKey, supplierName, threadId, text, mode });
  });
  const { fillDraft } = composer;
  useEffect(() => {
    if (!robbieDraft) return;
    const thread = threadForDraft(threads, robbieDraft);
    if (!thread) return;
    fillDraft(thread, robbieDraft.text, robbieDraft.mode);
    setRedirectThreadId(thread.id);
    setRobbieDraft(null);
  }, [robbieDraft, threads, fillDraft]);

  /**
   * Removes a not-yet-contacted supplier from outreach by taking them off the
   * component's shortlist (reversible from Supplier Discovery). Only offered
   * before anything has been sent — a started conversation is history.
   */
  const removeUncontactedThread = async (threadId: string): Promise<boolean> => {
    const thread = threads.find((t) => t.id === threadId);
    if (!thread || thread.status !== 'not_sent' || !bomId || !thread.componentId || isDiscardingThread) return false;
    setIsDiscardingThread(true);
    try {
      const remaining = shortlistWithoutSupplier(componentsToUse, thread.componentId, thread.supplierName);
      await bomClient.shortlistSuppliersBatch(bomId, {
        components: [{ componentId: thread.componentId, suppliers: remaining }],
      });
      await fetchLatestBOM({ silent: true });
      return true;
    } catch (error: any) {
      console.error('Error removing supplier from outreach:', error);
      setDiscardError(apiErrorMessage(error, 'I could not remove this draft. Try again.'));
      return false;
    } finally {
      setIsDiscardingThread(false);
    }
  };

  return {
    threads,
    activeThread,
    actionIndex: actionFlow.actionIndex,
    kickoffPending: actionFlow.kickoffPending,
    stageActions: composer.stageActions,
    stagedActionIds: composer.stagedActionIds,
    dismissAction: actionFlow.dismissAction,
    messageInput: composer.messageInput,
    setMessageInput: composer.setMessageInput,
    isLoadingBOM,
    isSendingThread: sendFlow.isSendingThread,
    retrySend: sendFlow.retrySend,
    refreshActions: actionFlow.refreshActions,
    sendError: sendFlow.sendError ?? discardError,
    dismissSendError: () => {
      sendFlow.dismissSendError();
      setDiscardError(null);
    },
    handleSendMessage: composer.handleSendMessage,
    removeUncontactedThread,
    isDiscardingThread,
    saveContactEmail: (email: string) => contactEmail.saveContactEmail(activeThread, email),
    isSavingEmail: contactEmail.isSavingEmail,
    emailSaveError: contactEmail.emailSaveError,
    decideSpec: (decision: SpecDecisionChoice) => specDecision.decideSpec(activeThread, decision),
    isSavingSpecDecision: specDecision.isSavingSpecDecision,
    specDecisionError: specDecision.specDecisionError,
    redirectThreadId,
    clearRedirectThreadId,
  };
};
